import type { FC } from 'react'
import { useCallback, useState } from 'react'
import { WizardModal } from '@/common/components'
import { useHostAddition } from '../hooks'
import { WelcomeStep } from './steps/WelcomeStep'
import { ConfigurationStep } from './steps/ConfigurationStep'
import { HostDisplayStep } from './steps/HostDisplayStep'
import type { HostFormState, WizardStep } from '../types'

interface HostAdditionModalProps {
  readonly open: boolean
  readonly onClose: () => void
}

const INITIAL_FORM: HostFormState = {
  hostName: '',
}

export const HostAdditionModal: FC<HostAdditionModalProps> = ({ open, onClose }) => {
  const [step, setStep] = useState<WizardStep>('welcome')
  const [form, setForm] = useState<HostFormState>(INITIAL_FORM)

  const { addHostAsync, isAdding, error, addedHost, reset } = useHostAddition()

  const handleClose = useCallback(() => {
    setStep('welcome')
    setForm(INITIAL_FORM)
    reset()
    onClose()
  }, [onClose, reset])

  const handleWelcomeNext = useCallback(() => {
    setStep('configuration')
  }, [])

  const handleConfigurationNext = useCallback(
    async (hostName: string) => {
      setForm({ hostName })
      const result = await addHostAsync({ hostName })
      if (result.success) {
        setStep('display')
      }
    },
    [addHostAsync],
  )

  const handleConfigurationBack = useCallback(() => {
    setStep('welcome')
  }, [])

  const hideCloseButton = step === 'display'

  return (
    <WizardModal open={open} onClose={handleClose} hideCloseButton={hideCloseButton}>
      {step === 'welcome' && <WelcomeStep onNext={handleWelcomeNext} onClose={handleClose} />}
      {step === 'configuration' && (
        <ConfigurationStep
          initialValue={form.hostName}
          isGenerating={isAdding}
          error={error}
          onGenerate={handleConfigurationNext}
          onBack={handleConfigurationBack}
        />
      )}
      {step === 'display' && addedHost && (
        <HostDisplayStep host={addedHost} onClose={handleClose} />
      )}
    </WizardModal>
  )
}
